import { useEffect, useState } from 'react';

import { useLiveWire } from '../wire';
import type { ConnectionEvent, WireClient } from '../wire';
import styles from './connection-status.module.css';

type BannerState = 'connecting' | 'reconnecting' | 'auth_expired' | null;

/** Maps a ConnectionEvent to the next banner state.
 *  Returns `undefined` to leave the current state unchanged. */
function nextBanner(ev: ConnectionEvent, current: BannerState): BannerState | undefined {
  switch (ev.kind) {
    case 'opened':
    case 'handshake_sent':
      return 'connecting';
    case 'active':
      return null;
    case 'auth_expired':
      return 'auth_expired';
    case 'closed':
      // If auth_expired already fired, keep that label until re-bootstrap.
      // Otherwise show 'reconnecting' — the retry loop is in progress.
      return current === 'auth_expired' ? current : 'reconnecting';
    default:
      return undefined;
  }
}

/**
 * Fixed-position banner that shows connection status during non-active phases.
 * Renders nothing when the connection is active or idle.
 *
 * Subscribes to the WireClient event stream via `useLiveWire` so it stays in
 * sync even across re-bootstraps (the client reference changes but
 * `useLiveWire` always holds the current one).
 */
export function ConnectionStatus(): JSX.Element | null {
  const [banner, setBanner] = useState<BannerState>(null);

  useEffect(() => {
    let unsubEvent: (() => void) | null = null;

    function subscribeToClient(client: WireClient | null): void {
      if (unsubEvent) {
        unsubEvent();
        unsubEvent = null;
      }
      if (!client) return;
      unsubEvent = client.onEvent((ev) => {
        setBanner((prev) => {
          const next = nextBanner(ev, prev);
          return next !== undefined ? next : prev;
        });
      });
    }

    // Subscribe to the current client immediately.
    subscribeToClient(useLiveWire.getState().client);

    // Re-subscribe whenever the client reference changes (re-bootstrap after
    // auth recovery swaps in a new WebSocketWireClient instance).
    const unsubStore = useLiveWire.subscribe((state, prev) => {
      if (state.client !== prev.client) {
        subscribeToClient(state.client);
        // New client means a fresh connect attempt is starting.
        setBanner(state.client ? 'connecting' : null);
      }
    });

    return () => {
      if (unsubEvent) unsubEvent();
      unsubStore();
    };
  }, []);

  if (!banner) return null;

  const text =
    banner === 'auth_expired'
      ? 'Session expired — reconnecting…'
      : banner === 'reconnecting'
        ? 'Reconnecting…'
        : 'Connecting…';

  const cls =
    banner === 'auth_expired'
      ? styles.authExpired
      : banner === 'reconnecting'
        ? styles.reconnecting
        : styles.connecting;

  return <div className={`${styles.banner} ${cls}`}>{text}</div>;
}
